import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_left, QIcon_chevron_right } from '@repo/icons/index'
import { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { s_pricing } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import PackageOptionCard from './PackageOptionCard'
import { useProductContext } from './ProductContext'

const PackageSelector = ({ products, isB2B = false }) => {
  const sliderRef = useRef(null)

  const [canGoPrev, setCanGoPrev] = useState(false)
  const [canGoNext, setCanGoNext] = useState(true)

  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { purchaseInfo, convertedCountryCode } = useProductContext()

  const initializeSlider = () => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(1)
      setTimeout(() => {
        setCanGoPrev(true)
        setCanGoNext(true)
      }, 50)
    }
  }

  useEffect(() => {
    if (sliderRef.current) {
      const totalSlides = sliderRef.current.props.children.length
      setCanGoPrev(products?.product_lines?.length > 4 || false)
      setCanGoNext(itemsPerSlide < totalSlides)
      initializeSlider()
    }
  }, [products])

  if (!products) return null

  const flexibleLessons = products.product_lines.filter((product) => !product.is_weekly_lesson_package)
  const weeklyLessons = products.product_lines.filter((product) => product.is_weekly_lesson_package)
  const currentProduct = isB2B ? flexibleLessons : weeklyLessons

  const itemsPerSlide = 4
  const totalProduct = currentProduct.length
  const showAsSlider = totalProduct > itemsPerSlide && !isMobile
  const isUnderFourCards = totalProduct < itemsPerSlide
  const isKorean = convertedCountryCode === 'KR'
  const lowestPricePackage = weeklyLessons.reduce((lowest, item) => {
    const itemPrice = item?.prices?.price_per_lesson?.value ?? Infinity
    const lowestPrice = lowest?.prices?.price_per_lesson?.value ?? Infinity

    return itemPrice < lowestPrice ? item : lowest
  }, null)

  const PackageSliderOption = {
    infinite: false,
    initialSlide: 1,
    speed: 500,
    slidesToShow: itemsPerSlide,
    slidesToScroll: 1,
    afterChange: (current: number) => {
      if (sliderRef.current) {
        const totalSlides = sliderRef.current.props.children.length
        setCanGoPrev(current > 0)
        setCanGoNext(current + itemsPerSlide < totalSlides)
      }
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  return (
    <section className="flex sm:flex-col flex-col-reverse	gap-y-16">
      {showAsSlider ? (
        <div className="relative">
          <div className="absolute left-1/2 top-1/2 w-[112%] transform -translate-x-1/2 -translate-y-1/2 flex justify-between items-center slider-buttons">
            <button
              className="rounded-full p-10"
              style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)' }}
              onClick={() => sliderRef.current?.slickPrev()}
              disabled={!canGoPrev}
            >
              <QIcon_chevron_left width={28} height={28} color={!canGoPrev ? COLORS.GRAY300 : COLORS.BLACK} />
            </button>
            <button
              className="rounded-full p-10"
              style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)' }}
              onClick={() => sliderRef.current?.slickNext()}
              disabled={!canGoNext}
            >
              <QIcon_chevron_right width={28} height={28} color={!canGoNext ? COLORS.GRAY300 : COLORS.BLACK} />
            </button>
          </div>

          <Slider ref={sliderRef} {...PackageSliderOption}>
            {weeklyLessons.map((product) => {
              const flexibleLesson = flexibleLessons.find((lesson) => lesson.name === product.name)
              return (
                <PackageOptionCard
                  key={product.id}
                  product={{ weeklyLesson: product, flexibleLesson }}
                  duration={purchaseInfo.duration.number}
                  currency={purchaseInfo.countryCode}
                />
              )
            })}
          </Slider>
          <style>
            {`
              .slick-list {
                margin-right: -14px;
              }
              .slick-slide {
                padding-right: 14px;
              }
            `}
          </style>
        </div>
      ) : (
        <section
          className={`w-full grid-cols-1 gap-x-12 gap-y-16 ${
            isUnderFourCards ? 'flex justify-center' : 'grid sm:grid-cols-4'
          }`}
        >
          {weeklyLessons.map((product) => {
            const flexibleLesson = flexibleLessons.find((lesson) => lesson.name === product.name)
            return (
              <PackageOptionCard
                key={product.id}
                product={{ weeklyLesson: product, flexibleLesson }}
                duration={purchaseInfo.duration.number}
                currency={purchaseInfo.countryCode}
                isLowestPrice={product.id === lowestPricePackage.id}
              />
            )
          })}
        </section>
      )}
      {!isB2B && !isKorean && (
        <ul>
          <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_usd)}</li>
          <li className="Body2-14Md text-gray-500">*{t(s_pricing.notice_exchange)}</li>
        </ul>
      )}
    </section>
  )
}

export default PackageSelector
